import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Trash2, Check, X, Edit3, Eye, Clock, Calendar,
  MapPin, Users, GraduationCap, PartyPopper, Mail, CheckCircle, XCircle,
  Upload, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  published: { label: 'Publie', color: 'bg-green-100 text-green-700' },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Refuse', color: 'bg-red-100 text-red-600' },
};

export default function AdminEventsManager({ getHeaders }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingEvent, setEditingEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(getEmptyForm());
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  function getEmptyForm() {
    return {
      title: '', description: '', event_type: 'event', date: '',
      start_time: '09:00', end_time: '17:00', organizer: '',
      location: 'Flexi-location, 304 Chaussee de Stockel',
      price: '', max_participants: '', contact_email: '', contact_phone: '', facebook_url: '',
      tags: '', image_url: ''
    };
  }

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/all`, {
        headers: getHeaders(), withCredentials: true
      });
      setEvents(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEvents(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = filter === 'all' ? events : events.filter(e => e.status === filter);
  const pendingCount = events.filter(e => e.status === 'pending').length;

  const handleApprove = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/approve`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success('Evenement approuve et publie');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/reject`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success('Evenement refuse');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleFull = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}/toggle-full`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Supprimer cet evenement ?')) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success('Evenement supprime');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image trop lourde (max 8 MB)');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setForm(f => ({ ...f, image_url: res.data.image_url }));
      toast.success('Image téléchargée');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.date) {
      toast.error('Titre et date requis');
      return;
    }
    // Defensive: never allow a data: URL to reach the DB — it breaks
    // social sharing (og:image needs a real http(s) URL) and bloats Mongo.
    if (form.image_url && form.image_url.startsWith('data:')) {
      toast.error("Merci d'utiliser le bouton d'upload pour cette image");
      return;
    }
    const payload = {
      ...form,
      price: form.price ? parseFloat(form.price) : 0,
      max_participants: form.max_participants ? parseInt(form.max_participants) : null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : []
    };
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent}`, payload, {
          headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true
        });
        toast.success('Evenement mis a jour');
      } else {
        await axios.post(`${API}/admin/events`, payload, {
          headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true
        });
        toast.success('Evenement cree et publie');
      }
      setShowForm(false);
      setEditingEvent(null);
      setForm(getEmptyForm());
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const startEdit = (evt) => {
    setForm({
      title: evt.title, description: evt.description, event_type: evt.event_type,
      date: evt.date, start_time: evt.start_time, end_time: evt.end_time,
      organizer: evt.organizer || '', location: evt.location || '',
      price: evt.price ? String(evt.price) : '', max_participants: evt.max_participants ? String(evt.max_participants) : '',
      contact_email: evt.contact_email || '', contact_phone: evt.contact_phone || '',
      facebook_url: evt.facebook_url || '',
      tags: (evt.tags || []).join(', '),
      image_url: evt.image_url && !evt.image_url.startsWith('data:') ? evt.image_url : ''
    });
    setEditingEvent(evt.event_id);
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="admin-events-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {['all', 'pending', 'published', 'rejected'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filter === f ? 'bg-[#1A3C34] text-white' : 'bg-white border border-[#E2E2DF] text-[#737373]'
                }`}
              >
                {f === 'all' ? `Tous (${events.length})` :
                 f === 'pending' ? `En attente (${pendingCount})` :
                 f === 'published' ? 'Publies' : 'Refuses'}
              </button>
            ))}
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => { setShowForm(!showForm); setEditingEvent(null); setForm(getEmptyForm()); }}
          className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 h-8 text-xs"
          data-testid="admin-add-event-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Creer un evenement
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3" data-testid="event-form">
          <h4 className="text-sm font-semibold text-[#1A3C34]">
            {editingEvent ? 'Modifier l\'evenement' : 'Nouvel evenement'}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">Titre *</label>
              <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="w-full mt-1 p-2 text-sm border border-[#E2E2DF] rounded-md resize-none h-20" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Type</label>
              <Select value={form.event_type} onValueChange={v => setForm(f => ({...f, event_type: v}))}>
                <SelectTrigger className="h-8 text-sm border-[#E2E2DF] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Evenement</SelectItem>
                  <SelectItem value="formation">Formation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#737373]">Date *</label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Heure debut</label>
              <Input type="time" value={form.start_time} onChange={e => setForm(f => ({...f, start_time: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Heure fin</label>
              <Input type="time" value={form.end_time} onChange={e => setForm(f => ({...f, end_time: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Organisateur</label>
              <Input value={form.organizer} onChange={e => setForm(f => ({...f, organizer: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Lieu</label>
              <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Prix (EUR, 0 = gratuit)</label>
              <Input type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Places max</label>
              <Input type="number" value={form.max_participants} onChange={e => setForm(f => ({...f, max_participants: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Email contact</label>
              <Input value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">Telephone</label>
              <Input value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">Image de l'événement</label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <div className="mt-1 flex items-start gap-3">
                <div className="w-24 h-24 rounded-md border border-[#E2E2DF] bg-[#F9F9F7] flex items-center justify-center overflow-hidden shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-[#E2E2DF]" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={uploading}
                      onClick={() => fileRef.current?.click()}
                      className="h-8 text-xs border-[#E2E2DF]"
                      data-testid="admin-event-upload-btn"
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      {uploading ? 'Upload...' : form.image_url ? 'Changer' : 'Télécharger'}
                    </Button>
                    {form.image_url && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                        className="h-8 text-xs border-red-200 text-red-500 hover:bg-red-50"
                      >
                        Retirer
                      </Button>
                    )}
                  </div>
                  <Input
                    value={form.image_url.startsWith('data:') ? '' : form.image_url}
                    onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    placeholder="ou coller une URL https://..."
                    className="h-8 text-xs border-[#E2E2DF]"
                  />
                  <p className="text-[10px] text-[#737373]">Max 8 MB. Pour un bon aperçu social, préférez un format 16:9.</p>
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">Lien Facebook (evenement / page)</label>
              <Input value={form.facebook_url} onChange={e => setForm(f => ({...f, facebook_url: e.target.value}))} placeholder="https://www.facebook.com/events/..." className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">Tags (separes par virgules)</label>
              <Input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} placeholder="networking, formation, gratuit..." className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={handleSave} className="bg-green-600 text-white hover:bg-green-700 h-8 text-xs">
              <Check className="w-3 h-3 mr-1" /> {editingEvent ? 'Mettre a jour' : 'Publier'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditingEvent(null); }} className="h-8 text-xs border-[#E2E2DF]">
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Events list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E2E2DF] rounded-md p-8 text-center">
          <Calendar className="w-8 h-8 text-[#E2E2DF] mx-auto mb-2" />
          <p className="text-sm text-[#737373]">Aucun evenement</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(evt => {
            const statusCfg = STATUS_CONFIG[evt.status] || STATUS_CONFIG.pending;
            return (
              <div key={evt.event_id} className="bg-white border border-[#E2E2DF] rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-event-${evt.event_id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-semibold text-[#1A3C34]">{evt.title}</span>
                    <Badge className={`text-[10px] ${statusCfg.color}`}>{statusCfg.label}</Badge>
                    <Badge className={`text-[10px] ${evt.event_type === 'formation' ? 'bg-[#4A90D9]/10 text-[#4A90D9]' : 'bg-[#C77D63]/10 text-[#C77D63]'}`}>
                      {evt.event_type === 'formation' ? 'Formation' : 'Evenement'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-[#737373] flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {evt.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {evt.start_time}-{evt.end_time}</span>
                    {evt.organizer && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {evt.organizer}</span>}
                    {evt.price > 0 && <span className="font-semibold text-[#1A3C34]">{evt.price} EUR</span>}
                    {evt.price === 0 && <Badge className="bg-green-50 text-green-600 text-[9px]">Gratuit</Badge>}
                  </div>
                  {evt.submitted_by && evt.submitted_by !== 'admin' && (
                    <div className="text-[10px] text-[#C77D63] mt-1">
                      <Mail className="w-2.5 h-2.5 inline mr-1" />
                      Soumis par {evt.submitted_by_name || evt.submitted_by_email || evt.submitted_by}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Full toggle */}
                  <label className="flex items-center gap-1 cursor-pointer" title="Marquer comme complet">
                    <input
                      type="checkbox"
                      checked={!!evt.is_full}
                      onChange={() => handleToggleFull(evt.event_id)}
                      className="w-3.5 h-3.5 rounded border-[#E2E2DF] text-red-600 focus:ring-red-500 cursor-pointer"
                      data-testid={`toggle-full-${evt.event_id}`}
                    />
                    <span className="text-[10px] text-[#737373]">Complet</span>
                  </label>
                  {evt.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => handleApprove(evt.event_id)} className="h-7 px-2 text-[10px] bg-green-600 text-white hover:bg-green-700" data-testid={`approve-${evt.event_id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approuver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(evt.event_id)} className="h-7 px-2 text-[10px] border-red-200 text-red-500 hover:bg-red-50" data-testid={`reject-${evt.event_id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Refuser
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => startEdit(evt)} className="h-7 px-2 text-[10px] border-[#E2E2DF]">
                    <Edit3 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(evt.event_id)} className="h-7 px-2 text-[10px] border-red-200 text-red-500 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
